interface ISubscriptionPlan {
  id: string
  isBookable: boolean
  features: PlanFeature[]
  pricing: PlanPricing[]
  year: number
  tier: PlanTier
  includedAiAccessMode?: 'None' | 'Limited'
}

export interface PlanAllowedChange {
  planId: string
  isImmediate: boolean
}

export interface PlanFeature {
  id: FeatureId
  available: boolean
  maxCount: number
}

export interface PlanPricing {
  planId: string
  currencyCode: Currency
  termInMonths: PlanTermMonths
  pricePerUser: number
  allowedChanges?: PlanAllowedChange[]
  isVolumePlan: boolean
}

/** Logical awork AI plan wire names shared with the backend. */
export const AiPlanNames = {
  /** The user occupies no awork AI seat. Assignment value only, never synchronized as a quota plan. */
  none: 'None',
  /** Marker plan of a seatless user in a paid workspace; the base plan's included allowance applies. */
  noAi: 'NoAi',
  starter: 'Starter',
  plus: 'Plus',
  pro: 'Pro',
  max: 'Max',
  ultra: 'Ultra'
} as const

export type AiPlanName = (typeof AiPlanNames)['starter' | 'plus' | 'pro' | 'max' | 'ultra']

/** Paid AI tiers in ascending order. Each uses one booked AI seat. */
export const PAID_AI_PLAN_NAMES: readonly AiPlanName[] = [
  AiPlanNames.starter,
  AiPlanNames.plus,
  AiPlanNames.pro,
  AiPlanNames.max,
  AiPlanNames.ultra
]

/** Default sponsored profile names of the global quota configuration. Support can rename or extend them. */
export const AiInternalProfileNames = {
  noCredits: 'NoCredits',
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  unlimited: 'Unlimited'
} as const

export type AiInternalProfileName = string

/** Normalized base-subscription plan names that fund included limited AI access. */
export const AiIncludedPlanNames = {
  basic: 'Basic',
  standard: 'Standard',
  professional: 'Professional',
  enterprise: 'Enterprise',
  enterprisePlus: 'EnterprisePlus',
  connect: 'Connect',
  other: 'Other'
} as const

export type AiIncludedPlanName = (typeof AiIncludedPlanNames)[keyof typeof AiIncludedPlanNames]
export type AiProfileName = AiPlanName | AiInternalProfileName | AiIncludedPlanName

export enum AiAccessSource {
  Paid = 'Paid',
  Sponsored = 'Sponsored',
  Trial = 'Trial',
  Included = 'Included'
}

export enum AiSubscriptionStatus {
  Inactive = 'inactive',
  Active = 'active',
  CancellationScheduled = 'cancellation_scheduled',
  Cancelled = 'cancelled'
}

export enum AiTrialStatus {
  NotStarted = 'not_started',
  InTrial = 'in_trial',
  Expired = 'trial_expired'
}

export enum AiUserPlanAssignmentStatus {
  Active = 'active',
  ChangeScheduled = 'change_scheduled',
  Inactive = 'inactive'
}

export enum AiQuotaMode {
  Blocked = 'Blocked',
  Metered = 'Metered',
  Unlimited = 'Unlimited'
}

export enum AiExecutionMode {
  Full = 'Full',
  ExhaustedQuotaFallback = 'ExhaustedQuotaFallback',
  Blocked = 'Blocked'
}

export enum AiBillingSyncStatus {
  Synced = 'synced',
  Pending = 'pending',
  Failed = 'failed'
}

export interface AiPlanCreditAllowance {
  plan: AiProfileName
  planType: AiAccessSource.Paid | AiAccessSource.Sponsored
  creditsIncluded: number | null
  isUnlimited: boolean
}

export interface AiUserSeatChangeQuote {
  plan: AiPlanName
  previousPlan: AiProfileName
  sourceVersion: number
  amountDueNow: number
  aiPricePerTerm: number
  currencyCode: string
  billingTermMonths: number
  effectiveOn?: string | null
}

export interface AiBundleEstimate {
  requiresRenewal: boolean
  amountDueNow: number
  currencyCode: string
  pmPricePerTerm: number
  aiPricePerTerm: number
  savingsPerPairedSeat: Partial<Record<AiPlanName, number>>
}

export interface AiBundleSeatRequest {
  planId?: string
  pmSeats?: number
  quantities: Record<AiPlanName, number>
  /** Current seats edited separately from quantities scheduled for renewal. */
  currentQuantities?: Record<AiPlanName, number>
  billingTermMonths: number
}

export interface AiPlanCatalogPrice {
  /** Upfront price per paired seat, keyed by the Chargebee PM parent item. */
  bundlePrices?: Record<string, number>
  addonPriceId: string
  currencyCode: Currency
  billingTermMonths: number
  pricePerUser: number
  isBookable: boolean
}

export interface AiPlanCatalog {
  plan: AiPlanName
  addonId: string
  pricing: AiPlanCatalogPrice[]
}

/**
 * Returns the committed awork AI seat quantity for the given base seats.
 * A fixed count (> 0) sets the AI seats independently of the base seats,
 * capped by the base seats. Without an override this helper returns its input; normal AI billing uses eligible users.
 * @param {number} bookedSeats The booked base-subscription seats.
 * @param {number} [aiBookedSeats=0] The fixed AI seat count, or 0 for the default.
 * @returns {number} The committed awork AI seat quantity.
 */
export function getCommittedAiSeats(bookedSeats: number, aiBookedSeats = 0): number {
  if (bookedSeats <= 0 || aiBookedSeats <= 0) {
    return bookedSeats
  }

  return Math.min(bookedSeats, aiBookedSeats)
}

export interface PlanCatalogResponse {
  plans: SubscriptionPlan[]
  aiPlans: AiPlanCatalog[]
  bundleAiPlans?: AiPlanCatalog[]
}

export interface AiPlanQuantities {
  starterQuantity: number
  plusQuantity: number
  proQuantity: number
  maxQuantity: number
  ultraQuantity?: number
}

export interface AiWorkspaceSubscription {
  supportsUserSeatChanges?: boolean
  pricingModel?: 'Legacy' | 'Bundle'
  scheduledAiSeats?: Partial<Record<AiPlanName, number>> | null
  scheduledAiSeatsEffectiveOn?: string | null
  scheduledAiBillingTermMonths?: number | null
  accessSource: AiAccessSource
  includedPlan?: AiIncludedPlanName | null
  defaultInternalProfile?: AiInternalProfileName | null
  sponsoredReason?: string | null
  status: AiSubscriptionStatus
  aiTrialStatus?: AiTrialStatus
  canStartAiTrial?: boolean
  aiTrialStartedAt?: string | null
  aiTrialExpiredAt?: string | null
  bookedSeats: number
  /** Active eligible users, including pending invitations. */
  eligibleUserCount?: number
  /** Fixed awork AI seat count, or 0 when AI seats follow eligible users. */
  aiBookedSeats?: number
  currentPeriodStartOn?: string | null
  currentPeriodEndOn?: string | null
  quotaResetAnchorOn?: string | null
  nextQuotaResetOn?: string | null
  firstAworkAiActivatedOn?: string | null
  quotaPlanOverride?: AiPlanName | null
  quotaPlanOverrideEndsOn?: string | null
  cancellationEffectiveOn?: string | null
  currencyCode?: Currency | null
  billingTermMonths?: number | null
  quantities: AiPlanQuantities
  billingSyncStatus: AiBillingSyncStatus
}

export interface AiUserPlanAssignment {
  pmProduct?: boolean | null
  userId: string
  plan: AiProfileName
  scheduledPlan?: AiProfileName | null
  scheduledPlanEffectiveOn?: string | null
  status: AiUserPlanAssignmentStatus
}

export interface AiUserQuotaStatus {
  userId: string
  accessSource: AiAccessSource
  quotaMode: AiQuotaMode
  isUnlimited: boolean
  isBlocked: boolean
  executionMode?: AiExecutionMode
  blockingLimitRole?: string | null
  availableAgainOn?: string | null
  canUpgrade: boolean
  plan: AiProfileName
  quotaPlan: AiProfileName
  activationBoostEndsOn?: string | null
  creditsIncluded?: number | null
  creditsUsed?: number | null
  creditsAvailable?: number | null
  creditsUsedPercentage?: number | null
  primaryPeriodEndOn?: string | null
}

export enum PlanTermMonths {
  Monthly = 1,
  Yearly = 12,
  Sixteen = 16,
  BiYearly = 24
}

export enum PlanTier {
  AiWorkspace = 0,
  Basic = 1,
  Standard = 2,
  Professional = 3,
  Enterprise = 4,
  EnterprisePlus = 5
}

export enum FeatureId {
  /**
   * Projects and limits to the number of projects.
   */
  Projects = 'projects',

  /**
   * Project Autopilot.
   */
  Autopilot = 'autopilot',

  /**
   * Project automations.
   */
  Automations = 'automations',

  /**
   * User absences.
   */
  Absences = 'absences',

  /**
   * Private projects.
   */
  PrivateProjects = 'privateProjects',

  /**
   * Global time reports.
   */
  GlobalTimeReports = 'globalTimeReports',

  /**
   * Workspace-wide time tracking settings.
   */
  TimeTrackingSettings = 'timeTrackingSettings',

  /**
   * Team structure in the workspace settings.
   */
  TeamStructure = 'teamStructure',

  /**
   * Custom fields for tasks.
   */
  CustomFields = 'customFields',

  /**
   * Custom roles and permissions.
   */
  CustomRoles = 'customRoles',

  /**
   * Custom subdomain for the workspace.
   */
  CustomSubdomain = 'customSubdomain',

  /**
   * Project and task templates.
   */
  Templates = 'templates',

  /**
   * The planner workload charts and calculations.
   */
  PlannerWorkload = 'plannerWorkload',

  /**
   * The ability to invite users to Connect, disabled in basic connect plan.
   */
  ConnectInvites = 'connectInvites',

  /**
   * Regular integrations such as Slack, Google, etc.
   */
  RegularIntegrations = 'regularIntegrations',

  /**
   * Advanced integrations such as Personio.
   */
  AdvancedIntegrations = 'advancedIntegrations',

  /**
   * Workspace-wide data import for tasks and project data.
   */
  DataImport = 'dataImport',

  /**
   * SSO and SCIM.
   */
  Sso = 'sso',

  /**
   * Documents
   */
  Documents = 'documents',

  /**
   * Project documents
   */
  ProjectDocuments = 'projectDocuments',

  /**
   * Private documents
   */
  PrivateDocuments = 'privateDocuments',

  /**
   * Space documents
   */
  SpaceDocuments = 'spaceDocuments',

  /**
   * Custom spaces and space permissions for docs.
   */
  DocumentSpaces = 'documentSpaces'
}

export enum PlanId {
  AiWorkspace2027 = 'ai-workspace-2027',
  EnterprisePlus2025 = 'enterpriseplus-2025',
  Enterprise2025 = 'enterprise-2025',
  Professional2025 = 'professional-2025',
  Standard2025 = 'standard-2025',
  Basic2025 = 'basic-2025',
  Connect2025 = 'connect-2025',
  Connect2024 = 'connect-2024',
  Enterprise2023 = 'enterprise-2023',
  Business2023 = 'business-2023',
  Team2023 = 'team-2023',
  AworkBusiness = 'awork-business',
  AworkStandard = 'awork-standard',
  AworkEnterprise = 'awork-enterprise',
  QStandard = 'q-standard',
  QEnterprise = 'q-enterprise',
  // ------------- Pricing plans: Legacy --------------- //
  Internal = 'internalfree',
  DeveloperFree = 'developer-free',
  Free = 'free',
  NewFree = 'free-new',
  LegacyStandard = 'standard',
  LegacyEnterprise = 'enterprise',
  LegacyBuhl = 'buhl'
}

export enum Currency {
  USD = 'USD',
  CHF = 'CHF',
  GBP = 'GBP',
  EUR = 'EUR'
}

export enum CurrencySymbol {
  EUR = '€',
  CHF = 'CHF',
  GBP = '£',
  USD = '$'
}

export enum CurrencySymbolPlacement {
  Left,
  Right
}

export const Currencies = {
  [Currency.EUR]: {
    symbol: CurrencySymbol.EUR,
    currency: Currency.EUR,
    abbreviationPlacement: CurrencySymbolPlacement.Right,
    symbolPlacement: CurrencySymbolPlacement.Right
  },
  [Currency.CHF]: {
    symbol: CurrencySymbol.CHF,
    currency: Currency.CHF,
    abbreviationPlacement: CurrencySymbolPlacement.Right,
    symbolPlacement: CurrencySymbolPlacement.Right
  },
  [Currency.GBP]: {
    symbol: CurrencySymbol.GBP,
    currency: Currency.GBP,
    abbreviationPlacement: CurrencySymbolPlacement.Left,
    symbolPlacement: CurrencySymbolPlacement.Left
  },
  [Currency.USD]: {
    symbol: CurrencySymbol.USD,
    currency: Currency.USD,
    abbreviationPlacement: CurrencySymbolPlacement.Left,
    symbolPlacement: CurrencySymbolPlacement.Left
  }
}

/**
 * The model of the subscription plan.
 * These plans are the ones available from the API.
 */
export class SubscriptionPlan implements ISubscriptionPlan {
  id: PlanId
  isBookable: boolean
  features: PlanFeature[]
  pricing: PlanPricing[]
  year: number
  tier: PlanTier
  includedAiAccessMode?: 'None' | 'Limited'

  constructor(subscriptionPlan: Partial<ISubscriptionPlan>) {
    Object.assign(this, subscriptionPlan)
  }
}
